import * as React from 'react'
import type { CSSProperties } from 'react'
import * as ToastPrimitives from '@radix-ui/react-toast'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'

export const ToastProvider = ToastPrimitives.Provider

export const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, style, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    style={{ ...styles.viewport, ...style }}
    className={className}
    {...props}
  />
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

export type ToastVariant = 'default' | 'success' | 'warning' | 'danger'

export interface ToastProps
  extends React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> {
  variant?: ToastVariant
}

export const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  ToastProps
>(({ className, variant = 'default', style, ...props }, ref) => (
  <ToastPrimitives.Root
    ref={ref}
    style={{
      ...styles.toast,
      ...variantStyles[variant],
      ...style,
    }}
    className={cn('toast', className)}
    {...props}
  />
))
Toast.displayName = ToastPrimitives.Root.displayName

export const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, style, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    style={{ ...styles.action, ...style }}
    className={cn('button-secondary', className)}
    {...props}
  />
))
ToastAction.displayName = ToastPrimitives.Action.displayName

export const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, style, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    style={{ ...styles.close, ...style }}
    className={className}
    {...props}
  >
    <X style={{ width: '16px', height: '16px' }} />
  </ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

export const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, style, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    style={{ ...styles.title, ...style }}
    className={className}
    {...props}
  />
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

export const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, style, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    style={{ ...styles.description, ...style }}
    className={className}
    {...props}
  />
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

export type ToastActionElement = React.ReactElement<typeof ToastAction>

const styles: Record<string, CSSProperties> = {
  viewport: {
    position: 'fixed',
    bottom: '16px',
    right: '16px',
    zIndex: 100,
    display: 'flex',
    maxHeight: '100vh',
    width: '100%',
    maxWidth: '420px',
    flexDirection: 'column',
    gap: '8px',
    padding: 0,
  },
  toast: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    overflow: 'hidden',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-secondary)',
    padding: '16px 24px 16px 16px',
    boxShadow: 'var(--shadow-md)',
    transition: 'all var(--transition-fast)',
    pointerEvents: 'auto',
    position: 'relative',
  },
  action: {
    display: 'inline-flex',
    height: '32px',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    background: 'transparent',
    padding: '0 12px',
    fontSize: '14px',
    fontWeight: 500,
    transition: 'background var(--transition-fast)',
    cursor: 'pointer',
    color: 'var(--text-primary)',
  },
  close: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    borderRadius: 'var(--radius-sm)',
    padding: '4px',
    color: 'var(--text-secondary)',
    opacity: 0.7,
    transition: 'opacity var(--transition-fast)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
  },
  title: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  description: {
    fontSize: '14px',
    opacity: 0.9,
    color: 'var(--text-secondary)',
  },
}

const variantStyles: Record<ToastVariant, CSSProperties> = {
  default: {},
  success: {
    borderColor: 'var(--success)',
  },
  warning: {
    borderColor: 'var(--warning)',
  },
  danger: {
    borderColor: 'var(--error)',
  },
}
